import React from 'react';
import { pleriRouter } from '../../../Pleri';
import { useChatStore } from '../store/chatStore';
import type { ChatMetrics, ContextItem } from '../store/chatStore';
import { useSidebarStore } from '../../../store/sidebarStore';
import type { PrintMode } from '../../../utils/pdfUtils';

declare global {
  interface Window {
    pleriChat?: {
      updateChatInput?: (options: UpdateChatInputOptions) => void;
      navigate?: (options: NavigateOptions) => void;
    };
  }
}

export interface UpdateChatInputOptions {
  prompt?: string;
  context?: ContextItem[];
  autoSubmit?: boolean;
  appendContext?: boolean;
  useExistingConversation?: boolean;
  metrics?: ChatMetrics;
  focusedView?: {
    content: React.ReactNode;
    title?: string;
    printMode?: PrintMode;
  };
  chatInformation?: React.ReactNode;
  selectedToolGroups?: string[];
}

export interface NavigateOptions {
  to: string;
  search?: Record<string, string>;
  replace?: boolean;
}

export function registerPleriApi() {
  if (typeof window !== 'undefined') {
    window.pleriChat = window.pleriChat || {};
    window.pleriChat.updateChatInput = async function (
      options: UpdateChatInputOptions,
    ): Promise<void> {
      const {
        prompt,
        context,
        autoSubmit,
        appendContext,
        useExistingConversation,
        focusedView,
        chatInformation,
      } = options;
      const chatButton = document.getElementById('pleri-chat-open-button');
      const chatBox = document.getElementById('pleri-chat-box');
      const isOpen =
        chatBox && chatBox.querySelector('.fixed.bottom-6.right-6.opacity-100');
      if (chatButton && !isOpen) {
        (chatButton as HTMLElement).click();
        useChatStore.getState().setProgrammaticOpen(true);
      }

      // If not using existing conversation, start a new one
      if (!useExistingConversation) {
        const store = useChatStore.getState();
        const newId = store.createConversation();
        store.setActiveConversation({ conversationId: newId });
        // Set chat information after creating conversation (since createConversation clears it)
        if (chatInformation !== undefined) {
          store.setChatInformation(chatInformation);
        }
      }

      const activeConversation = useChatStore.getState().activeConversationId;

      pleriRouter.navigate({
        to: '/c/$conversationId',
        params: {
          conversationId: activeConversation || 'new',
        },
      });
      // Merge context items by id if appending
      if (Array.isArray(context)) {
        if (appendContext) {
          const prev = useChatStore.getState().attachedContextItems;
          const merged = [...prev];
          context.forEach((item) => {
            const idx = merged.findIndex((i) => i.id === item.id);
            if (idx !== -1) {
              merged[idx] = item; // Replace existing
            } else {
              merged.push(item); // Add new
            }
          });
          useChatStore.getState().setAttachedContextItems(merged);
        } else {
          useChatStore.getState().setAttachedContextItems(context);
        }
      }
      if (options.metrics) {
        useChatStore.getState().setPendingMetrics(options.metrics);
      }

      // Handle focused view if provided
      if (focusedView && activeConversation) {
        const store = useChatStore.getState();
        store.setFocusedViewContent(
          focusedView.content,
          focusedView.title,
          focusedView.printMode,
        );
        store.setFocusedViewOpen(activeConversation, true);
        // Close sidebar when opening focus view via PleriClient
        useSidebarStore.getState().closeSidebar();
      }

      // Handle chat information if provided (only if using existing conversation)
      if (chatInformation !== undefined && useExistingConversation) {
        useChatStore.getState().setChatInformation(chatInformation);
      }

      if (typeof prompt === 'string') {
        useChatStore.getState().setInputPrompt(prompt);
      }
      if (autoSubmit) {
        const previousToolGroups = useChatStore.getState().selectedToolGroups;
        if (options.selectedToolGroups) {
          useChatStore
            .getState()
            .setSelectedToolGroups(options.selectedToolGroups);
        }
        await useChatStore.getState().submitInputPrompt();
        if (options.selectedToolGroups) {
          useChatStore.getState().setSelectedToolGroups(previousToolGroups);
        }
      } else {
        useChatStore.getState().activeInputRef?.current?.focus();
      }
    };
    window.pleriChat.navigate = function ({
      to,
      search,
      replace = false,
    }: NavigateOptions): void {
      const chatButton = document.getElementById('pleri-chat-open-button');
      const chatBox = document.getElementById('pleri-chat-box');
      const isOpen =
        chatBox && chatBox.querySelector('.fixed.bottom-6.right-6.opacity-100');
      if (chatButton && !isOpen) {
        (chatButton as HTMLElement).click();
        useChatStore.getState().setProgrammaticOpen(true);
      }

      pleriRouter.navigate({
        to,
        search,
        replace,
      });
    };
  }
}
